import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { useToggleState } from '@anytag/shared-hooks';
import { PackageProposalSocialAcccountBudgetInput } from '@anytag/graphql';
import { Button, BaseModal, SelectionBar, SelectionBarProps } from '@anytag/shared-atoms';
import { PackageInfluencersDownloadButton } from '../PackageDownloadButton';

interface PackageActionBarProps extends Omit<SelectionBarProps, 'children'> {
  packageId?: number | null;
  isViewMode?: boolean;
  numberSelected: number;
  hasAllSelected: boolean;
  isRemoving?: boolean;
  onSelectAll: () => void;
  onToggleSelect: () => void;
  selectedInfluencerAccountIds: number[];
  onToggleBulkEditModal: () => void;
  isSocialMediaAccountsSelection?: boolean;
  onRemove: (callback: () => void) => void;
  selectedSocialAccounts: PackageProposalSocialAcccountBudgetInput[];
}

export const PackageActionBar = ({
  packageId,
  isViewMode,
  onSelectAll,
  onRemove,
  isRemoving,
  numberSelected,
  hasAllSelected,
  onToggleSelect,
  onToggleBulkEditModal,
  selectedInfluencerAccountIds
}: PackageActionBarProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const removeModal = useToggleState();

  return (
    <SelectionBar
      selectAllProps={{ onClick: onSelectAll }}
      checkboxProps={{
        onClick: onToggleSelect,
        indeterminate: !hasAllSelected,
        label: t('Selected', { count: numberSelected })
      }}
    >
      {isViewMode ? (
        <div css={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          {isAdminStaff && (
            <>
              <Button
                variant="solid"
                color="danger"
                onClick={removeModal.open}
                title={t('Button.Remove')}
                className="px-6"
              />
              <BaseModal
                open={removeModal.status}
                onClose={removeModal.close}
                title={t('Modal.RemoveInfluencerTitle')}
                description={t('Modal.RemoveInfluencerDescription')}
                footer={{
                  cancelButton: { onClick: removeModal.close },
                  submitButton: {
                    title: t('Button.Remove'),
                    onClick: () => onRemove(removeModal.close),
                    loading: { showIcon: true, status: isRemoving }
                  }
                }}
              />
            </>
          )}
          <PackageInfluencersDownloadButton
            packageId={Number(packageId)}
            selectedInfluencerIds={selectedInfluencerAccountIds}
          />
        </div>
      ) : (
        <Button variant="outline" title={t('Button.Bulk Edit')} onClick={onToggleBulkEditModal} className="ml-4 px-4" />
      )}
    </SelectionBar>
  );
};
